import React, { useCallback, useEffect, useRef, useState } from "react";
import { cloneDeep, get } from "lodash";
import styled from "styled-components/macro";

import { Preset, IntroConfig } from "../../models/modelSelector/IntroConfig";
import { ModelItem, ModelItemType, ModelStepsMap } from "../../models/modelSelector/ModelItem";

import { ImagesContainer } from "./ImagesContainer";
import { ModelSelectorButtonBack } from "./ButtonBack";

import { getImageUrlForConfig } from "../../utilities/modelSelectorDataUtils";
import { getModelFromConfig } from "../../utilities/configUtils";
import GaReporter from "../../utilities/gaReporter";

export type ModelSelectorProps = {
    composedUrl: string;
    guiResourcesUrl: string;
    introConfig: IntroConfig;
    accountId: string | null;
    onSelectConfig: (configString: string, configEncoding?: string, surfaceProductArticleNo?: string) => void;
    /** When set (e.g. user came from configurator back button), back at step 0 navigates to configurator with this config. */
    preservedConfigFromUrl?: { config: string; encType?: string } | null;
};

const StepNameContainer = styled.div`
    position: absolute;
    z-index: 2;
    top: 25px;
    left: 25px;
    min-width: 200px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 600px) and (orientation: landscape) {
        max-width: 160px;
        min-width: "unset";
    }
`;

const StepName = styled.div`
    font-size: 22px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 250px;
    height: 50px;
    border-radius: 8px;
    color: ${({ theme }: { theme: any }) => get(theme, "modelSelector.step.color", "white")};
    background: ${({ theme }: { theme: any }) =>
        get(theme, "modelSelector.step.backgroundColor", "var(--primary-color, #1976d2)")};
    border-bottom: ${({ theme }: { theme: any }) => get(theme, "modelSelector.step.borderBottom", "none")};
    @media (max-width: 600px) and (orientation: landscape) {
        font-size: 16px;
    }
`;

export const ModelSelector = ({
    composedUrl,
    guiResourcesUrl,
    introConfig,
    accountId,
    onSelectConfig,
    preservedConfigFromUrl = null,
}: ModelSelectorProps) => {
    const [items, setItems] = useState<ModelStepsMap>({});
    const [, setLogo] = useState("");
    const [stepTexts, setStepTexts] = useState<{ [key: string]: string | undefined }>({});
    const [stepIndex, setStepIndex] = useState(0);
    const [selectedItem, setSelectedItem] = useState("root");
    const [breadcrumbs, setBreadcrumbs] = useState<string[]>([]);
    const loadedNodesRef = useRef<Set<string>>(new Set());
    const loadingNodesRef = useRef<Record<string, Promise<void>>>({});
    const scheduledChildrenRef = useRef<Set<string>>(new Set());
    const nodeSetsRef = useRef<Record<string, Preset[]>>({});

    const getImageUrl = useCallback(
        async (config: string, market: string | undefined, serieName: string, width: number, height: number) => {
            try {
                return await getImageUrlForConfig(config, market, serieName, composedUrl, width, height, accountId);
            } catch {
                return null;
            }
        },
        [accountId, composedUrl]
    );

    const getItems = useCallback(
        async (presets: Preset[], parent: string): Promise<ModelItem[]> => {
            let height = Math.ceil(presets.length > 3 ? (window.innerHeight - 8) / 2 : window.innerHeight);
            let width = 0;

            if (window.devicePixelRatio) {
                height = Math.round(height * window.devicePixelRatio);
            }

            if (window.innerWidth <= 1024) {
                height = 0;
                width = Math.round(window.innerWidth * window.devicePixelRatio);
                if (width % 2 === 1) {
                    width = 2 * Math.round(width / 2);
                }
            }

            const urlParams = new URLSearchParams(window.location.search);
            const market = urlParams.get("market") || undefined;

            const result: (ModelItem | null)[] = await Promise.all(
                presets.map(async (preset, i) => {
                    const imageUrl = await getImageUrl(preset.config, market, preset.serie, width, height);
                    if (!imageUrl) {
                        return null;
                    }

                    return {
                        id: `${parent}_${i}`,
                        imageUrl,
                        config: preset.sets ? null : preset.config,
                        name: preset.name,
                        type: preset.sets ? ModelItemType.MAIN : ModelItemType.TEMPLATES,
                    };
                })
            );

            return result.filter((item): item is ModelItem => item != null);
        },
        [getImageUrl]
    );

    const registerChildNodes = useCallback(
        (parentId: string, presets: Preset[], level: number) => {
            const nextStepTexts: { [key: string]: string | undefined } = {};

            presets.forEach((preset, index) => {
                if (!preset.sets || preset.sets.length === 0) return;

                const childId = `${parentId}_${index}`;
                nodeSetsRef.current[childId] = preset.sets;
                nextStepTexts[childId] = preset.stepText || introConfig.stepTexts[level + 1];
            });

            if (Object.keys(nextStepTexts).length > 0) {
                setStepTexts(prev => ({
                    ...prev,
                    ...nextStepTexts,
                }));
            }
        },
        [introConfig.stepTexts]
    );

    const loadNode = useCallback(
        async (nodeId: string, sets: Preset[], level: number) => {
            if (loadedNodesRef.current.has(nodeId)) return;

            const existingLoad = loadingNodesRef.current[nodeId];
            if (existingLoad) {
                await existingLoad;
                return;
            }

            nodeSetsRef.current[nodeId] = sets;

            const loadPromise = getItems(sets, nodeId)
                .then(subItems => {
                    loadedNodesRef.current.add(nodeId);
                    setItems(prev => ({
                        ...prev,
                        [nodeId]: subItems,
                    }));
                    registerChildNodes(nodeId, sets, level);
                })
                .finally(() => {
                    delete loadingNodesRef.current[nodeId];
                });

            loadingNodesRef.current[nodeId] = loadPromise;
            await loadPromise;
        },
        [getItems, registerChildNodes]
    );

    const preloadDirectChildren = useCallback(
        async (parentId: string, level: number) => {
            if (scheduledChildrenRef.current.has(parentId)) return;

            const childSets = nodeSetsRef.current[parentId];
            if (!childSets) return;

            scheduledChildrenRef.current.add(parentId);

            await Promise.all(
                childSets.map((preset, index) => {
                    if (!preset.sets || preset.sets.length === 0) return Promise.resolve();

                    const childId = `${parentId}_${index}`;
                    return loadNode(childId, preset.sets, level + 1);
                })
            );
        },
        [loadNode]
    );

    useEffect(() => {
        if (!introConfig) return;

        loadedNodesRef.current.clear();
        loadingNodesRef.current = {};
        scheduledChildrenRef.current.clear();
        nodeSetsRef.current = { root: introConfig.sets };

        setItems({});
        setSelectedItem("root");
        setBreadcrumbs([]);
        setStepIndex(0);
        setStepTexts({ root: introConfig.stepTexts[0] });
        setLogo(guiResourcesUrl + introConfig.logo);

        let isActive = true;

        const loadRoot = async () => {
            await loadNode("root", introConfig.sets, 0);

            if (isActive) {
                await preloadDirectChildren("root", 0);
            }
        };

        void loadRoot();

        return () => {
            isActive = false;
        };
    }, [guiResourcesUrl, introConfig, loadNode, preloadDirectChildren]);

    useEffect(() => {
        if (selectedItem === "root") return;

        const selectedSets = nodeSetsRef.current[selectedItem];
        if (!selectedSets) return;

        let isActive = true;

        const loadSelectedBranch = async () => {
            await loadNode(selectedItem, selectedSets, stepIndex);

            if (isActive) {
                await preloadDirectChildren(selectedItem, stepIndex);
            }
        };

        void loadSelectedBranch();

        return () => {
            isActive = false;
        };
    }, [loadNode, preloadDirectChildren, selectedItem, stepIndex]);

    const onItemClick = (item: ModelItem) => {
        const { id, name, config } = item;
        if (config) {
            // When user has a preserved config from URL and clicks the same room (same model), open with their config.
            const configToUse =
                preservedConfigFromUrl?.config &&
                getModelFromConfig(config).localeCompare(getModelFromConfig(preservedConfigFromUrl.config), "en", {
                    sensitivity: "base",
                }) === 0
                    ? preservedConfigFromUrl.config
                    : config;

            const encTypeToUse =
                configToUse === preservedConfigFromUrl?.config ? preservedConfigFromUrl.encType : undefined;

            GaReporter.event(`model_selector_step${stepIndex + 1}`, "ModelSelector", name, {
                event_callback: GaReporter.createHitCallback(() => onSelectConfig(configToUse, encTypeToUse)),
            });
            return;
        }

        GaReporter.event(`model_selector_step${stepIndex + 1}`, "ModelSelector", name);
        const breadcrumbsUpdated: Array<string> = cloneDeep(breadcrumbs);
        breadcrumbsUpdated.push(id);
        setSelectedItem(id);
        setBreadcrumbs(breadcrumbsUpdated);
        setStepIndex(stepIndex + 1);
    };

    const onBackClick = () => {
        const breadcrumbsUpdated: Array<string> = breadcrumbs.slice(0, breadcrumbs.length - 1);
        const newStepIndex = Math.max(0, stepIndex - 1);

        setSelectedItem(breadcrumbsUpdated[breadcrumbsUpdated.length - 1] || "root");
        setBreadcrumbs(breadcrumbsUpdated);
        setStepIndex(newStepIndex);
    };

    if (!items[selectedItem]) return null;

    const showBackButton = stepIndex > 0;
    const currentStepText = stepTexts[selectedItem];
    return (
        <>
            {currentStepText ? (
                <StepNameContainer>
                    <StepName>
                        <span>{currentStepText}</span>
                    </StepName>
                </StepNameContainer>
            ) : null}
            <ImagesContainer key={selectedItem} items={items[selectedItem]} onItemClick={onItemClick} />
            {showBackButton ? <ModelSelectorButtonBack onClick={onBackClick} /> : null}
        </>
    );
};
